import { useTranslation } from "react-i18next";
import { FileUpload } from "~/common/components/FileUpload";
import { Stack, Typography, type SxProps, type Theme } from "~/common/mui";
import type { ConfiguratorAttachmentController } from "../../application/useConfiguratorAttachmentController";
import type { ConfigurationAttachmentSubstepViewModel } from "./substepViewModel";

export interface ConfigurationAttachmentSubstepProps {
  viewModel: ConfigurationAttachmentSubstepViewModel;
  controller?: ConfiguratorAttachmentController;
  className?: string;
  sx?: SxProps<Theme>;
}

const summaryLabels = {
  cancelUpload: "Cancel upload",
  deleteAttachment: "Delete attachment",
  deleting: "Deleting attachment",
  dismissFeedback: "Dismiss",
  reconciling: "Syncing attachment",
  uploading: "Uploading",
};

export const ConfigurationAttachmentSubstep = ({
  viewModel,
  controller,
  className,
  sx,
}: ConfigurationAttachmentSubstepProps) => {
  const { t } = useTranslation();
  const controllerViewModel = controller?.viewModel ?? { confirmedAttachment: null, pendingOperation: null };
  const hasConfirmedAttachment = Boolean(controllerViewModel.confirmedAttachment || viewModel.attachment);
  const pending = Boolean(controllerViewModel.pendingOperation);
  const disabled = Boolean(viewModel.disabled || pending || hasConfirmedAttachment || !controller);

  return (
    <Stack gap={1} className={className} sx={sx}>
      {viewModel.title ? <Typography variant="bodyLarge">{viewModel.title}</Typography> : null}
      {viewModel.description ? (
        <Typography variant="bodyMedium" color="text.secondary">
          {viewModel.description}
        </Typography>
      ) : null}
      {controller ? (
        <FileUpload>
          <FileUpload.Area
            accept="application/pdf,.pdf"
            activeDropLabel={t("feature.configurator.v2.attachment.dropActive", "Drop PDF drawing here")}
            disabled={disabled}
            disabledText={
              hasConfirmedAttachment
                ? t(
                    "feature.configurator.v2.attachment.deleteFirst",
                    "Delete the existing drawing before uploading a new one.",
                  )
                : undefined
            }
            dropLabel={t("feature.configurator.v2.attachment.drop", "or drop a PDF drawing here")}
            fileInputLabel={t("feature.configurator.v2.attachment.select", "Select a file to upload")}
            formatText={viewModel.formatText ?? t("feature.configurator.v2.attachment.format", "PDF only, max. 10 MiB")}
            onFilesSelected={controller.onFilesSelected}
            selectLabel={t("feature.configurator.v2.attachment.selectAction", "Select a file")}
          />
          <FileUpload.Summary
            labels={{
              cancelUpload: t("feature.configurator.v2.attachment.cancel", summaryLabels.cancelUpload),
              deleteAttachment: t("feature.configurator.v2.attachment.delete", summaryLabels.deleteAttachment),
              deleting: t("feature.configurator.v2.attachment.deleting", summaryLabels.deleting),
              dismissFeedback: t("feature.configurator.v2.attachment.dismiss", summaryLabels.dismissFeedback),
              reconciling: t("feature.configurator.v2.attachment.reconciling", summaryLabels.reconciling),
              uploading: t("feature.configurator.v2.attachment.uploading", summaryLabels.uploading),
            }}
            onCancelUpload={controller.cancelUpload}
            onDelete={controller.deleteAttachment}
            onDismissFeedback={controller.dismissFeedback}
            viewModel={controllerViewModel}
          />
        </FileUpload>
      ) : null}
    </Stack>
  );
};
